import { useMutation, useQuery } from "@tanstack/react-query";
import { ApiResponse } from "../../apis/data";
import { queryKey } from "../../enumerations/queryKey";
import { cleanedParams } from "../../plugins/utils";
import {
  OrderCancelParams,
  OrderDetailParams,
  OrderDetailResponse,
  OrderFeedbackCreateParams,
  OrderFeedbackNewParams,
  OrderListParams,
  OrderListResponse,
  OrderRefundParams,
  RefundDataParams,
  RefundDataResponse,
  RefundPolicyResponse,
  UserOrderDetailParams,
  UserOrderDetailResponse,
} from "./data";
import {
  apiGetOrderDetail,
  apiGetOrderFeedbackNew,
  apiGetOrderList,
  apiGetRefuncPolicy,
  apiGetRefundData,
  apiGetUserOrderDetail,
  apiPostOrderCancel,
  apiPostOrderFeedbackCreate,
  apiPostOrderRefund,
} from "./service";

export const useGetOrderList = (params: OrderListParams, options: any) => {
  const queryKeyArr = [
    queryKey.ORDER_LIST,
    ...Object.values(cleanedParams(params)),
  ];

  return useQuery<OrderListResponse[]>({
    queryKey: queryKeyArr,
    queryFn: () =>
      apiGetOrderList(params).then(
        (res: ApiResponse<OrderListResponse[]>) => res.data.data
      ),
    ...options,
  });
};

export const useGetOrderDetail = (params: OrderDetailParams, options: any) => {
  const queryKeyArr = [
    queryKey.ORDER_DETAIL,
    ...Object.values(cleanedParams(params)),
  ];

  return useQuery<OrderDetailResponse>({
    queryKey: queryKeyArr,
    queryFn: () =>
      apiGetOrderDetail(params).then(
        (res: ApiResponse<OrderDetailResponse>) => res.data.data
      ),
    ...options,
  });
};

export const useGetOrderFeedbackNew = (
  params: OrderFeedbackNewParams,
  options: any
) => {
  const queryKeyArr = [
    queryKey.ORDER_FEEDBACK_NEW,
    ...Object.values(cleanedParams(params)),
  ];

  return useQuery<OrderDetailResponse>({
    queryKey: queryKeyArr,
    queryFn: () =>
      apiGetOrderFeedbackNew(params).then(
        (res: ApiResponse<OrderDetailResponse>) => res.data.data
      ),
    ...options,
  });
};

export const useOrderCancel = () => {
  return useMutation({
    mutationFn: ({ params }: { params: OrderCancelParams }) =>
      apiPostOrderCancel(params),
  });
};

export const useOrderFeedbackCreate = () => {
  return useMutation({
    mutationFn: ({ params }: { params: OrderFeedbackCreateParams }) =>
      apiPostOrderFeedbackCreate(params),
  });
};

export const useOrderRefund = () => {
  return useMutation({
    mutationFn: ({ params }: { params: OrderRefundParams }) =>
      apiPostOrderRefund(params),
  });
};

export const useGetRefundPolicy = (options: any) => {
  return useQuery<RefundPolicyResponse>({
    queryKey: [queryKey.REFUND_POLICY],
    queryFn: () =>
      apiGetRefuncPolicy().then((res: ApiResponse<RefundPolicyResponse>) => {
        return res.data.data;
      }),
    ...options,
  });
};

export const useGetUserOrderDetail = (
  params: UserOrderDetailParams,
  options: any
) => {
  const queryKeyArr = [
    queryKey.USER_ORDER_DETAIL,
    ...Object.values(cleanedParams(params)),
  ];
  return useQuery<UserOrderDetailResponse>({
    queryKey: queryKeyArr,
    queryFn: () =>
      apiGetUserOrderDetail(params).then(
        (res: ApiResponse<UserOrderDetailResponse>) => {
          return res.data.data;
        }
      ),
    ...options,
  });
};

export const useGetRefundData = (params: RefundDataParams, options: any) => {
  const queryKeyArr = [
    queryKey.REFUND_DATA,
    ...Object.values(cleanedParams(params)),
  ];
  return useQuery<RefundDataResponse>({
    queryKey: queryKeyArr,
    queryFn: () =>
      apiGetRefundData(params).then((res: ApiResponse<RefundDataResponse>) => {
        return res.data.data;
      }),
    ...options,
  });
};
